import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { useStore } from "@/store/StoreContext";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";

const field = "w-full bg-transparent border border-white/15 px-5 py-4 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors";

export default function Account() {
  const { user, setUser, logout } = useStore();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    }
  }, [user]);

  if (user === null) return <div className="pt-16 min-h-screen" data-testid="account-loading" />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      setUser(data);
      toast.success(mode === "register" ? "Account created. Welcome to Jae Noir." : "Welcome back.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-16" data-testid="account-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-24 sm:pb-32">
        {user ? (
          <div className="max-w-2xl">
            <Reveal><Eyebrow>Account</Eyebrow></Reveal>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl mt-4 leading-[0.9]">
              <MaskedLine delay={0.1}><span className="text-cream">{user.name || "Member"}</span></MaskedLine>
            </h1>
            <Reveal delay={0.2}>
              <div className="mt-12 border border-white/10 p-8 sm:p-10 space-y-6" data-testid="account-profile">
                <div className="flex justify-between items-center border-b border-white/10 pb-6">
                  <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/40">Email</span>
                  <span className="text-sm" data-testid="account-email">{user.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/40">Status</span>
                  <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-gold" data-testid="account-status">{user.role === "admin" ? "Founders Circle" : "Noir Member"}</span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10" data-testid="account-orders">
                <h2 className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-5">Orders</h2>
                {orders === null ? (
                  <div className="border border-white/10 p-6 animate-pulse h-20" />
                ) : orders.length === 0 ? (
                  <div className="border border-white/10 p-8 text-center">
                    <p className="text-sm text-cream/40 mb-4" data-testid="account-orders-empty">No orders yet. The bag is waiting.</p>
                    <Link to="/shop" className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-gold hover:text-cream transition-colors" data-testid="account-orders-shop-link">Shop the Drop</Link>
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {orders.map((o) => (
                      <li key={o.order_id} className="border border-white/10 p-6" data-testid={`order-${o.order_id}`}>
                        <div className="flex justify-between items-center mb-4">
                          <span className="font-mono2 text-xs text-gold">{o.order_id}</span>
                          <span className={`font-mono2 text-[9px] tracking-[0.2em] uppercase px-2 py-1 border ${o.status === "shipped" ? "bg-cream text-noir-bg border-cream" : "bg-gold/10 border-gold/40 text-gold"}`} data-testid={`order-status-${o.order_id}`}>
                            {o.status === "shipped" ? "Shipped" : "Paid"}
                          </span>
                        </div>
                        <ul className="space-y-1 mb-4">
                          {o.items.map((i, idx) => (
                            <li key={idx} className="text-sm text-cream/60">{i.qty} × {i.name} — Size {i.size}</li>
                          ))}
                        </ul>
                        {o.tracking_number && (
                          <p className="text-xs text-cream/50 mb-4" data-testid={`order-tracking-${o.order_id}`}>
                            {o.carrier} — <span className="font-mono2 text-gold">{o.tracking_number}</span>
                          </p>
                        )}
                        <div className="flex justify-between text-xs font-mono2 text-cream/40">
                          <span>{new Date(o.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
                          <span className="text-gold">{money(o.amount)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 flex gap-4">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className="bg-gold text-noir-bg px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-cream transition-colors duration-300"
                    data-testid="admin-dashboard-link"
                  >
                    Command Room
                  </Link>
                )}
                <button
                  onClick={async () => { await logout(); toast.success("Signed out. Move different."); }}
                  className="border border-white/20 px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase text-cream/70 hover:border-gold hover:text-gold transition-colors duration-300"
                  data-testid="logout-button"
                >
                  Sign Out
                </button>
              </div>
            </Reveal>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <Reveal><Eyebrow className="text-center">{mode === "login" ? "Welcome Back" : "Join the House"}</Eyebrow></Reveal>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl mt-4 text-center leading-[0.9]" data-testid="account-form-title">
              {mode === "login" ? "Sign In" : "Create Account"}
            </h1>

            <Reveal delay={0.15}>
              <div className="flex mt-10 border border-white/10" data-testid="account-mode-tabs">
                {["login", "register"].map((m) => (
                  <button
                    key={m}
                    onClick={() => { setMode(m); setError(""); }}
                    className={`flex-1 py-3 font-mono2 text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${mode === m ? "bg-gold text-noir-bg" : "text-cream/50 hover:text-gold"}`}
                    data-testid={`account-tab-${m}`}
                  >
                    {m === "login" ? "Sign In" : "Register"}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="account-form">
                {mode === "register" && (
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={field} data-testid="register-name-input" />
                )}
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={field} data-testid="account-email-input" />
                <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (8+ characters)" className={field} data-testid="account-password-input" />
                {error && <p className="text-red-400 text-xs font-mono2 tracking-wide" data-testid="account-error">{error}</p>}
                <button type="submit" disabled={busy} className="w-full bg-gold text-noir-bg font-mono2 text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-cream transition-colors duration-300 disabled:opacity-50" data-testid="account-submit-button">
                  {busy ? "One moment…" : mode === "login" ? "Sign In" : "Create Account"}
                </button>
              </form>
              <p className="mt-6 text-center text-xs text-cream/40">
                Members get early drop access and order tracking on every purchase.
              </p>
            </Reveal>
          </div>
        )}
      </div>
    </div>
  );
}
